/**
 * Two Wheeler Finance (TWF) Insert API Service
 * Handles two-wheeler financing application submission
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/twfInsertApi.ts
 * Logic preserved AS-IS, using Redux instead of SolidJS signals
 */

import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { API_RESPONSE_CODES, BFL_WHEELES_ERROR, OPERATION_IDS } from "./constants";
import { ITwfInsertApiRequest, ITwfInsertApiResponse } from "../types/twfInsertTypes";

/**
 * Insert two-wheeler finance application
 *
 * @param data - Two-wheeler finance application request data
 * @returns Promise with two-wheeler finance insert response
 *
 * @example
 * ```typescript
 * const response = await twfInsert({
 *   mobileNo: "9876543210",
 *   journeyName: "TWF_FRESH",
 *   vehicleMake: "Honda",
 *   vehicleModel: "Activa",
 *   loanAmount: "50000"
 * });
 * ```
 */
export const twfInsert = async (data: ITwfInsertApiRequest): Promise<ITwfInsertApiResponse> => {
  try {
    const response = (await request({
      operationId: OPERATION_IDS.twfInsert,
      requestData: { ...data },
    })) as ITwfInsertApiResponse;

    // TWF accepts both 7001 (success) and 7005 (partial success) status codes
    if (
      response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001 &&
      response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7005
    ) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    logPageActionToNewRelic(BFL_WHEELES_ERROR, {
      component: "Two Wheeler Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    return {} as ITwfInsertApiResponse;
  }
};
